import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { GridColumnMenu } from "@mui/x-data-grid";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
} from "@mui/material";
import PropTypes from "prop-types";

import Modal from "Components/Common/Modal";
import { billingOptionsForDispatchBills, getOptionLabel } from "common/enums";
import Backdrop from "Components/Common/Backdrop";
import SelectField from "Components/Common/SelectField";

import {
  useLazyBillingCalculationQuery,
  usePostDispatchBillsMutation,
} from "apis/Billing";

import { StyledGrid } from "./Style";

const CustomColumnMenu = (props) => (
  <GridColumnMenu
    {...props}
    slots={{
      columnMenuColumnsItem: null,
      columnMenuSortItem: null,
    }}
  />
);

const DispatchBillModel = ({
  handleClose,
  billCycleId,
  subscriptionType,
  t,
}) => {
  const [
    getBillingCalculations,
    {
      data: { data: billingCalculations } = {},
      isLoading: isBillingCalculationLoading,
      isFetching: isBillingCalculationFetching,
    },
  ] = useLazyBillingCalculationQuery();

  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [query, setQuery] = useState({
    billingCycleId: billCycleId,
    calculationStatus: 2,
  });
  const [{ isSMS, isEmail }, setCommunicationMode] = useState({
    isSMS: false,
    isEmail: false,
  });
  const [isRowSelected, setIsRowSelected] = useState(false);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });

  const [dispatchBills, { isLoading }] = usePostDispatchBillsMutation();

  const columns = [
    {
      field: "consumerNumber",
      headerName: "Consumer Number",
      width: 150,
    },
    { field: "consumerName", headerName: "Consumer Name", width: 350 },

    {
      field: "billCycle",
      headerName: "Bill Cycle",
      width: 150,
    },
    {
      field: "status",
      headerName: t("Status"),
      renderCell: ({ row: { status } }) =>
        getOptionLabel("billCalculationStatus", status),
      width: 190,
    },
    {
      field: "meterNumber",
      headerName: "Meter Number",
      width: 150,
    },

    {
      field: "netPayable",
      headerName: "Net Payable",
    },
  ];

  const submitBtnHandler = async () => {
    try {
      if (!isEmail && !isSMS) {
        toast.error("Select notification mode");
      } else {
        await dispatchBills(billCycleId, {
          notifyModes:
            isEmail && isSMS ? [1, 2] : isEmail ? [1] : isSMS ? [2] : "",

          billCalculationIds: selectedRowIds,
        });
        handleClose();
      }
    } catch (error) {}
  };

  useEffect(() => {
    (async () => {
      await getBillingCalculations(query.billingCycleId, {
        calculationStatus: query.calculationStatus,
        subscriptionType,
      });
    })();
  }, [query]);

  useEffect(() => {
    if (!selectedRowIds.length && billingCalculations) {
      setSelectedRowIds(billingCalculations);
    }
  }, [selectedRowIds]);

  return (
    <>
      <Backdrop
        open={isBillingCalculationFetching || isBillingCalculationLoading}
      />
      <Modal
        isOpen
        handleClose={handleClose}
        title="Dispatch Bills"
        maxWidth="lg"
      >
        <>
          <Grid container columnSpacing={2} className="mt-2 mb-2">
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <SelectField
                className="w-100 mt-1"
                size="small"
                label="Status"
                options={billingOptionsForDispatchBills}
                value={billingOptionsForDispatchBills.find(
                  ({ value }) => value === query.calculationStatus
                )}
                onChange={(_, { value }) =>
                  setQuery((prev) => ({
                    ...prev,
                    calculationStatus: value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={6} sm={2} md={2} lg={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={isEmail}
                    onChange={() =>
                      setCommunicationMode((prev) => ({
                        ...prev,
                        isEmail: !prev.isEmail,
                      }))
                    }
                  />
                }
                label={t("Email")}
              />
            </Grid>
            <Grid item xs={6} sm={2} md={2} lg={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={isSMS}
                    onChange={() =>
                      setCommunicationMode((prev) => ({
                        ...prev,
                        isSMS: !prev.isSMS,
                      }))
                    }
                  />
                }
                label={t("SMS")}
              />
            </Grid>
          </Grid>
          <StyledGrid
            checkboxSelection
            disableRowSelectionOnClick
            rows={billingCalculations ?? []}
            columns={columns}
            pageSizeOptions={[5, 10]}
            rowCount={billingCalculations?.length}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            onRowSelectionModelChange={(ids) => {
              const selectedIDs = Array.from(
                // eslint-disable-next-line no-undef
                new Set([...ids.flatMap((id) => id)])
              );
              setIsRowSelected(!!selectedIDs.length);
              setSelectedRowIds(selectedIDs);
            }}
            slots={{
              columnMenu: CustomColumnMenu,
              noRowsOverlay: () => (
                <Stack
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                >
                  {t("No records found")}.
                </Stack>
              ),
            }}
            keepNonExistentRowsSelected
            autoHeight
          />
          <div className="text-end">
            <Button
              variant="contained"
              className="mt-2"
              disabled={isLoading || !isRowSelected}
              onClick={submitBtnHandler}
            >
              {t("Submit")} {isLoading && <CircularProgress size={20} />}
            </Button>
          </div>
        </>
      </Modal>
    </>
  );
};

DispatchBillModel.propTypes = {
  handleClose: PropTypes.func,
  billCycleId: PropTypes.number,
  subscriptionType: PropTypes.number,
  t: PropTypes.func,
};

export default withTranslation()(DispatchBillModel);
